import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { HelpCircle, FileText, CalendarDays, ShieldCheck, Heart, CalendarPlus, Mail } from "lucide-react";

const OPT = {
  question: { icon: HelpCircle, bg: "#eaf1fb", fg: "#1e3a8a", iconFg: "#2563eb", label: "Ask a Tax Question" },
  quote: { icon: FileText, bg: "#e9f4ee", fg: "#166534", iconFg: "#16a34a", label: "Get a Quote" },
  consult: { icon: CalendarDays, bg: "#fdeee4", fg: "#c2410c", iconFg: "#ea580c", label: "Book a Free Consult" },
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const qp = (k) => new URLSearchParams(window.location.search).get(k);

const post = (path, data) => axios.post(`${API}/widget${path}`, data).then((r) => r.data);
const get = (path) => axios.get(`${API}/widget${path}`).then((r) => r.data);
const track = (firmId, event, convId) => { try { post("/event", { firm_id: firmId, event, conversation_id: convId || null }); } catch (e) { /* ignore */ } };
const downloadIcs = (ics) => {
  try {
    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "consultation.ics";
    document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) { /* ignore */ }
};

function notifyParent(type, extra = {}) {
  try { window.parent.postMessage({ __taxflow_widget: true, type, ...extra }, "*"); } catch (e) { /* ignore */ }
}

const QUOTE_QS = [
  ["country", "Which country do you live in?"],
  ["citizenship", "What's your citizenship?"],
  ["filing_year", "Which tax year(s) do you need to file?"],
  ["filing_status", "Filing status (single, married, etc.)?"],
  ["income", "What types of income do you have (employment, self-employment, investments, crypto)?"],
  ["extras", "Any foreign accounts, business ownership, IRS notices, or missed prior-year filings?"],
];

function fmtText(text) {
  // Lightweight **bold** rendering + clickable URLs (Penny replies use markdown + links).
  return String(text).split(/\*\*/).map((p, i) => {
    const nodes = String(p).split(/(https?:\/\/[^\s]+)/g).map((seg, j) =>
      /^https?:\/\//.test(seg)
        ? <a key={j} href={seg} target="_blank" rel="noreferrer" style={{ color: "#2563eb", wordBreak: "break-all" }}>{seg}</a>
        : seg);
    return i % 2 === 1 ? <strong key={i}>{nodes}</strong> : <span key={i}>{nodes}</span>;
  });
}

export default function WidgetApp() {
  const firmId = qp("firm") || "expat-tax-firm";
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(qp("open") === "1");
  const [screen, setScreen] = useState("menu"); // menu | chat | contact | quote | consult
  const [convId, setConvId] = useState(null);
  const [path, setPath] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [contact, setContact] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [contactDone, setContactDone] = useState(false);
  const [prequal, setPrequal] = useState(null);
  const [afterContact, setAfterContact] = useState(null);
  const [greeting, setGreeting] = useState(false);
  const [leadMode, setLeadMode] = useState(false);
  const [leadName, setLeadName] = useState("");
  const [portalUrl, setPortalUrl] = useState("");
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailText, setEmailText] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [coldNudged, setColdNudged] = useState(false);
  const bottomRef = useRef(null);
  const lastMsgRef = useRef(null);
  const coldTimerRef = useRef(null);

  useEffect(() => { get(`/config?firm_id=${firmId}`).then(setCfg).catch(() => {}); }, [firmId]);
  useEffect(() => {
    let vid = null;
    try { vid = localStorage.getItem("tf_visitor_id"); } catch (e) { /* ignore */ }
    if (!vid) return;
    get(`/recognize?firm_id=${firmId}&visitor_id=${encodeURIComponent(vid)}`)
      .then((d) => { if (d && d.lead_mode) { setLeadMode(true); setLeadName(d.lead_name || ""); setPortalUrl(d.portal_url || ""); } })
      .catch(() => {});
  }, [firmId]);
  useEffect(() => {
    // Iframe is transparent — make the widget document transparent so the host page shows through.
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    const root = document.getElementById("root");
    if (root) root.style.background = "transparent";
  }, []);
  useEffect(() => {
    const last = msgs[msgs.length - 1];
    if (!last) return;
    // For Penny's replies, land at the TOP of the answer so the reader can scroll at their own pace.
    // For the visitor's own messages, scroll to the bottom as usual.
    if (last.from === "penny" && msgs.length > 1) {
      lastMsgRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [msgs]);
  useEffect(() => { notifyParent(open ? "open" : greeting ? "peek" : "close"); }, [open, greeting]);

  // Proactive greeting bubble ~8s after load (once per browser session).
  useEffect(() => {
    if (open) return;
    let dismissed = false;
    try { dismissed = sessionStorage.getItem("tf_greeted") === "1"; } catch (e) { /* ignore */ }
    if (dismissed) return;
    const t = setTimeout(() => { if (!open) { setGreeting(true); track(firmId, "bubble_shown"); } }, 8000);
    return () => clearTimeout(t);
  }, [open]);

  const dismissGreeting = (e) => {
    if (e) e.stopPropagation();
    setGreeting(false);
    try { sessionStorage.setItem("tf_greeted", "1"); } catch (err) { /* ignore */ }
  };
  const openFromGreeting = () => { try { sessionStorage.setItem("tf_greeted", "1"); } catch (e) { /* ignore */ } setGreeting(false); setOpen(true); track(firmId, "opened", convId); };

  const color = cfg?.primary_color || "#2563eb";
  const accent = cfg?.accent_color || "#1f2a44";

  const ensureSession = useCallback(async (chosenPath) => {
    if (convId) return convId;
    let vid = null;
    try {
      vid = localStorage.getItem("tf_visitor_id");
      if (!vid) { vid = "v-" + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("tf_visitor_id", vid); }
    } catch (e) { /* ignore */ }
    const data = await post("/session", { firm_id: firmId, path: chosenPath, page_url: document.referrer || window.location.href, visitor_id: vid });
    setConvId(data.conversation_id);
    if (data.reused && Array.isArray(data.messages) && data.messages.length) {
      setMsgs(data.messages); // resume the returning visitor's transcript (no new inquiry created)
      if (data.cold_nudged) setColdNudged(true); // Penny already made her last-attempt on return
    } else {
      setMsgs([{ from: "penny", text: data.welcome_message }]);
    }
    applyCaptured(data.contact, data.prequal);
    if (data.lead_mode) { setLeadMode(true); setLeadName(data.lead_name || ""); setPortalUrl(data.portal_url || ""); }
    return data.conversation_id;
  }, [convId, firmId]);

  const startPath = async (p) => {
    setPath(p);
    const cid = await ensureSession(p);
    if (p === "question") {
      setScreen("chat");
    } else if (p === "quote") {
      setScreen("chat");
      setMsgs((m) => [...m, { from: "penny", text: cfg?.consult ? "Happy to help you get a quote! Our fees depend on your situation. First, let me grab a few quick details." : "Let's get you a quote." }]);
      setAfterContact("quote");
      setScreen("contact");
    } else if (p === "consult") {
      setAfterContact("consult");
      setScreen("contact");
    }
    void cid;
  };

  const naturalPause = async (startedAt, reply) => {
    // Human-like cadence: Penny "reads" the message, then "types" the reply at a
    // realistic human speed (WPM). Real model latency counts toward the wait, so
    // we never stack excessive artificial delay on top of a slow response.
    const PENNY_WPM = 55;          // realistic human typing pace (words / minute)
    const READ_LEAD_MS = 800;      // brief pause to read & consider the client's message
    const MIN_MS = 1200;           // never feel instant/robotic
    const MAX_MS = 9000;           // never make the client wait excessively
    const words = String(reply || "").trim().split(/\s+/).filter(Boolean).length;
    const typeMs = (words / PENNY_WPM) * 60000;
    const target = Math.min(MAX_MS, Math.max(MIN_MS, READ_LEAD_MS + typeMs));
    const wait = Math.max(0, target - (Date.now() - startedAt));
    if (wait > 0) await new Promise((r) => setTimeout(r, wait));
  };

  const send = async () => {
    if (!input.trim() || busy) return;
    const text = input.trim();
    setInput("");
    setMsgs((m) => [...m, { from: "you", text }]);
    setBusy(true);
    const startedAt = Date.now();
    try {
      const cid = await ensureSession(path);
      const res = await post("/message", { conversation_id: cid, message: text, path });
      await naturalPause(startedAt, res.reply);
      setMsgs((m) => [...m, { from: "penny", text: res.reply, escalated: res.escalated }]);
      applyCaptured(res.contact, res.prequal);
      if (res.lead_mode) { setLeadMode(true); setLeadName(res.lead_name || leadName); setPortalUrl(res.portal_url || portalUrl); }
      if (res.open_scheduler && !res.lead_mode) {
        const c = res.contact || {};
        const done = c.first_name && c.last_name && c.email;
        setPath("consult"); setAfterContact("consult");
        setScreen(done ? "consult" : "contact");
      }
    } catch (e) {
      setMsgs((m) => [...m, { from: "penny", text: "Sorry, I hit a snag. Please try again in a moment." }]);
    } finally { setBusy(false); }
  };

  // Sync contact details Penny captured conversationally so forms prefill (never re-ask).
  const applyCaptured = (cap, pq) => {
    if (cap) {
      setContact((c) => ({
        first_name: c.first_name || cap.first_name || "",
        last_name: c.last_name || cap.last_name || "",
        email: c.email || cap.email || "",
        phone: c.phone || cap.phone || "",
      }));
      if (cap.first_name && cap.last_name && cap.email) setContactDone(true);
    }
    if (pq && Object.keys(pq).length) setPrequal(pq);
  };

  // Last-ditch email capture: if a no-contact chat goes quiet, Penny makes ONE friendly attempt to
  // capture an email before the visitor slips away. Re-armed on every new message; backend enforces
  // the one-time + 60-minute-window rules.
  useEffect(() => {
    if (coldTimerRef.current) { clearTimeout(coldTimerRef.current); coldTimerRef.current = null; }
    const eligible = open && screen === "chat" && convId && !contactDone && !leadMode && !coldNudged && !busy;
    if (!eligible) return;
    const quietMs = Math.max(3, Number(cfg?.cold_nudge_quiet_seconds || 120)) * 1000;
    coldTimerRef.current = setTimeout(async () => {
      try {
        const res = await post("/cold-nudge", { conversation_id: convId });
        if (res && res.sent && res.message) {
          setColdNudged(true);
          setMsgs((m) => [...m, { from: "penny", text: res.message }]);
        }
      } catch (e) { /* ignore */ }
    }, quietMs);
    return () => { if (coldTimerRef.current) { clearTimeout(coldTimerRef.current); coldTimerRef.current = null; } };
  }, [open, screen, convId, contactDone, leadMode, coldNudged, busy, msgs, cfg]);

  const submitContact = async () => {
    const req = cfg?.required_fields || ["first_name", "last_name", "email"];
    if (req.some((f) => !contact[f]?.trim())) return;
    setBusy(true);
    try {
      const cid = await ensureSession(path);
      await post("/contact", { conversation_id: cid, ...contact });
      setContactDone(true);
      if (afterContact === "quote") { setScreen("quote"); }
      else if (afterContact === "consult") { setScreen("consult"); }
      else { setScreen("chat"); setMsgs((m) => [...m, { from: "penny", text: `Thanks ${contact.first_name}! How can I help?` }]); }
    } catch (e) { /* ignore */ } finally { setBusy(false); }
  };

  const resetToMenu = () => { setScreen("menu"); setPath(null); };

  const goToPortal = () => {
    const u = portalUrl || `${process.env.REACT_APP_BACKEND_URL}/portal`;
    window.open(`${u}${u.includes("?") ? "&" : "?"}ref=chat`, "_blank", "noopener");
  };
  const submitEmail = async () => {
    if (!emailText.trim() || emailBusy) return;
    setEmailBusy(true);
    try {
      const cid = await ensureSession(path);
      await post("/lead/request", { conversation_id: cid, message: emailText.trim() });
      setEmailSent(true); setEmailText("");
    } catch (e) { /* ignore */ }
    finally { setEmailBusy(false); }
  };

  // Clickable quick-reply choices offered under Penny's replies.
  const chooseAction = (action) => {
    if (action === "quote") { setPath("quote"); setAfterContact("quote"); setScreen(contactDone ? "quote" : "contact"); }
    else if (action === "consult") { setPath("consult"); setAfterContact("consult"); setScreen(contactDone ? "consult" : "contact"); }
    else if (action === "contact") { setAfterContact("chat"); setScreen("contact"); }
  };
  const CHIPS = leadMode ? [] : [
    { key: "quote", label: "Get a Quote" },
    { key: "consult", label: "Book a Free Consult" },
    ...(!contactDone ? [{ key: "contact", label: "Share my details" }] : []),
  ];

  if (!cfg) return <div style={{ padding: 20, fontFamily: "system-ui" }}>Loading…</div>;

  const avatar = cfg.penny_avatar_url;
  const openTo = (key) => { try { sessionStorage.setItem("tf_greeted", "1"); } catch (e) { /* ignore */ } setGreeting(false); setOpen(true); track(firmId, "opened", convId); startPath(key); };

  const PennyHeader = ({ big }) => (
    <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
      <img src={avatar} alt="Penny" style={{ width: big ? 60 : 52, height: big ? 60 : 52, borderRadius: "50%", objectFit: "cover", border: "3px solid #fff", boxShadow: "0 3px 10px rgba(0,0,0,.18)", flexShrink: 0 }} />
      <div>
        <div style={{ fontSize: big ? 22 : 19, fontWeight: 800, color: accent, lineHeight: 1.05, display: "flex", alignItems: "center", gap: 6 }}>
          Hi, I'm Penny <Heart size={big ? 18 : 15} color="#f28c7a" fill="#f28c7a" />
        </div>
        <div style={{ fontSize: 12.5, color: "#475569", marginTop: 4, lineHeight: 1.3 }}>{cfg.penny_tagline}</div>
      </div>
    </div>
  );

  const OptionCards = () => (
    <div>
      {cfg.options.filter((o) => !leadMode || o.key === "question").map((o) => {
        const st = OPT[o.key] || OPT.question; const Ic = st.icon;
        return (
          <button key={o.key} onClick={() => openTo(o.key)} data-testid={`widget-option-${o.key}`}
            style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left", padding: "12px 14px",
              marginBottom: 10, background: st.bg, border: "none", borderRadius: 14, cursor: "pointer", transition: "transform .28s cubic-bezier(.22,1,.36,1)" }}
            onMouseEnter={(e) => (e.currentTarget.style.transform = "translateX(4px)")}
            onMouseLeave={(e) => (e.currentTarget.style.transform = "translateX(0)")}>
            <span style={{ width: 36, height: 36, borderRadius: 10, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Ic size={19} color={st.iconFg} />
            </span>
            <span style={{ fontSize: 14.5, fontWeight: 700, color: st.fg, lineHeight: 1.2 }}>{o.label || st.label}</span>
          </button>
        );
      })}
    </div>
  );

  const TrustFooter = () => (
    <div style={{ display: "flex", alignItems: "center", gap: 9, background: accent, color: "#fff", borderRadius: 12, padding: "10px 14px", marginTop: 6 }}>
      <ShieldCheck size={18} style={{ flexShrink: 0 }} />
      <span style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.25 }}>Trusted. Professional. Here for You.</span>
    </div>
  );

  const LeadBanner = () => (
    <div data-testid="widget-lead-banner"
      style={{ background: "#eef6ff", border: "1px solid #dbeafe", borderRadius: 14, padding: 14, marginBottom: 12 }}>
      <div style={{ fontSize: 13.5, fontWeight: 800, color: accent, marginBottom: 4 }}>
        Welcome back{leadName ? `, ${leadName}` : ""}!
      </div>
      <div style={{ fontSize: 12.5, color: "#475569", lineHeight: 1.45, marginBottom: 12 }}>
        You're already set up with us. To reschedule, manage your appointment, or upload documents, please use your Secure Portal. Have a question or another request? Email our team and we'll follow up.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <button onClick={goToPortal} data-testid="widget-lead-portal-btn"
          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "10px 12px",
            background: accent, color: "#fff", border: "none", borderRadius: 10, cursor: "pointer", fontSize: 13.5, fontWeight: 700 }}>
          <ShieldCheck size={16} /> Go to Secure Portal
        </button>
        <button onClick={() => { setEmailSent(false); setEmailOpen(true); }} data-testid="widget-lead-email-btn"
          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "10px 12px",
            background: "#fff", color: accent, border: `1.5px solid ${accent}`, borderRadius: 10, cursor: "pointer", fontSize: 13.5, fontWeight: 700 }}>
          <Mail size={16} /> Email Us
        </button>
      </div>
    </div>
  );

  const Launcher = (
    <div style={{ position: "fixed", bottom: 16, right: 16, width: 72, height: 72, transform: open ? "scale(0)" : "scale(1)", transition: "transform .2s" }}
      onMouseEnter={() => { if (!open) setGreeting(true); }}>
      <span aria-hidden style={{ position: "absolute", inset: 0, borderRadius: "50%", background: accent, animation: "tfRing 2s ease-out infinite" }} />
      <button onClick={() => { setOpen(true); track(firmId, "opened", convId); }} data-testid="widget-launcher" title={cfg.launcher_text}
        style={{ position: "absolute", inset: 0, width: 72, height: 72, borderRadius: "50%", padding: 0, border: "3px solid #fff",
          cursor: "pointer", boxShadow: "0 8px 24px rgba(0,0,0,.28)", overflow: "hidden", background: accent, display: "block" }}
        onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.06)")}
        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}>
        <img src={avatar} alt="Penny" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      </button>
    </div>
  );

  return (
    <div style={{ fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif" }}>
      {Launcher}
      {greeting && !open && (
        <div data-testid="widget-greeting"
          style={{ position: "fixed", right: 16, bottom: 100, width: "min(324px, calc(100vw - 32px))",
            background: "#fff", borderRadius: 18, boxShadow: "0 16px 44px rgba(0,0,0,.24)",
            padding: "18px 18px 16px", animation: "tfPop .55s cubic-bezier(.22,1,.36,1)", border: "1px solid #eef2f7" }}>
          <button onClick={dismissGreeting} data-testid="widget-greeting-close" aria-label="Dismiss"
            style={{ position: "absolute", top: 10, right: 12, background: "transparent", border: "none", color: "#94a3b8", fontSize: 19, cursor: "pointer", lineHeight: 1, zIndex: 2 }}>×</button>
          <PennyHeader />
          <div style={{ height: 1, background: "#eef2f7", margin: "4px 0 12px" }} />
          <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", marginBottom: 12 }}>What would you like to do?</div>
          <OptionCards />
          <TrustFooter />
        </div>
      )}
      <style>{`
        @keyframes tfPop{from{opacity:0;transform:translateY(14px) scale(.95)}to{opacity:1;transform:translateY(0) scale(1)}}
        @keyframes tfPanel{from{opacity:0;transform:translateY(28px) scale(.96)}to{opacity:1;transform:translateY(0) scale(1)}}
        @keyframes tfScreen{from{opacity:0;transform:translateX(22px)}to{opacity:1;transform:translateX(0)}}
        @keyframes tfMsg{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        @keyframes tfRing{0%{transform:scale(1);opacity:.55}70%{transform:scale(1.35);opacity:0}100%{transform:scale(1.35);opacity:0}}
        @keyframes tfDot{0%,60%,100%{transform:translateY(0);opacity:.4}30%{transform:translateY(-4px);opacity:1}}
      `}</style>
      {open && (
        <div data-testid="widget-panel" style={{ position: "fixed", bottom: 16, right: 16, width: "min(384px, calc(100vw - 24px))",
          height: "min(620px, calc(100vh - 32px))", background: "#fff", borderRadius: 18, overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,.3)", display: "flex", flexDirection: "column",
          animation: "tfPanel .55s cubic-bezier(.22,1,.36,1)" }}>
          {/* header */}
          <div style={{ background: accent, color: "#fff", padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
            <img src={avatar} alt="Penny" style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(255,255,255,.7)" }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>I'm {cfg.assistant_name} with {cfg.brand_name}</div>
              <div style={{ fontSize: 12, opacity: .85 }}>{cfg.penny_tagline}</div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="widget-close" style={{ background: "transparent", border: "none", color: "#fff", fontSize: 22, cursor: "pointer", lineHeight: 1 }}>×</button>
          </div>

          {/* body */}
          <div style={{ flex: 1, overflowY: "auto", padding: 16, background: "#f8fafc" }}>
            <div key={screen} style={{ animation: "tfScreen .5s cubic-bezier(.22,1,.36,1)" }}>
            {screen === "menu" && (
              <div>
                <PennyHeader big />
                <div style={{ height: 1, background: "#eef2f7", margin: "4px 0 12px" }} />
                {leadMode && <LeadBanner />}
                <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", marginBottom: 12 }}>{leadMode ? "You can also ask me a tax question:" : "What would you like to do?"}</div>
                <OptionCards />
                <TrustFooter />
              </div>
            )}

            {(screen === "chat") && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {leadMode && <LeadBanner />}
                {msgs.map((m, i) => (
                  <div key={i} ref={i === msgs.length - 1 ? lastMsgRef : null} data-testid={`widget-msg-${m.from}`} style={{ alignSelf: m.from === "you" ? "flex-end" : "flex-start", maxWidth: "82%",
                    background: m.from === "you" ? color : "#fff", color: m.from === "you" ? "#fff" : "#0f172a",
                    border: m.from === "you" ? "none" : "1px solid #e2e8f0", borderRadius: 14, padding: "10px 13px", fontSize: 14, whiteSpace: "pre-wrap", overflowWrap: "anywhere", wordBreak: "break-word", animation: "tfMsg .45s cubic-bezier(.22,1,.36,1)" }}>
                    {m.from === "penny" ? fmtText(m.text) : m.text}
                    {m.ics && (
                      <button onClick={() => downloadIcs(m.ics)} data-testid="widget-add-to-calendar"
                        style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 10, padding: "8px 12px",
                          background: color, color: "#fff", border: "none", borderRadius: 10, cursor: "pointer",
                          fontSize: 13, fontWeight: 700 }}>
                        <CalendarPlus size={16} /> Add to Calendar
                      </button>
                    )}
                    {m.portalUrl && (
                      <button onClick={() => window.open(`${m.portalUrl}${m.portalUrl.includes("?") ? "&" : "?"}ref=chat`, "_blank", "noopener")} data-testid="widget-activate-portal"
                        style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 10, padding: "8px 12px",
                          background: color, color: "#fff", border: "none", borderRadius: 10, cursor: "pointer",
                          fontSize: 13, fontWeight: 700 }}>
                        <ShieldCheck size={16} /> Activate My Portal
                      </button>
                    )}
                  </div>
                ))}
                {busy && (
                  <div data-testid="widget-typing" style={{ alignSelf: "flex-start", display: "flex", alignItems: "center", gap: 8, animation: "tfMsg .3s ease" }}>
                    <img src={avatar} alt="" style={{ width: 26, height: 26, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                    <div style={{ display: "flex", alignItems: "center", gap: 4, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: "11px 14px" }}>
                      {[0, 1, 2].map((i) => (
                        <span key={i} style={{ width: 7, height: 7, borderRadius: "50%", background: "#94a3b8", display: "inline-block", animation: `tfDot 1.2s ${i * 0.18}s infinite ease-in-out` }} />
                      ))}
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>
            )}

            {screen === "contact" && (
              <ContactForm cfg={cfg} contact={contact} setContact={setContact} onSubmit={submitContact} busy={busy} color={color} />
            )}

            {screen === "quote" && (
              <QuoteForm onDone={async (answers) => {
                setBusy(true);
                const cid = await ensureSession("quote");
                const res = await post("/quote", { conversation_id: cid, answers });
                setBusy(false); setScreen("chat");
                setMsgs((m) => [...m,
                  { from: "penny", text: `Based on what you shared, this looks like a "${res.recommended_service || "custom"}" — estimated ${res.price_range || "range on request"}. ${res.disclaimer}` },
                  { from: "penny", text: "Would you like to book a free consult to firm this up? Just tap 'Book a Free Consult' below." }]);
              }} busy={busy} color={color} />
            )}

            {screen === "consult" && (
              <ConsultForm cfg={cfg} firmId={firmId} contact={contact} prequal={prequal} onBooked={(res) => {
                  setScreen("chat");
                  setMsgs((m) => [...m, { from: "penny", text: res.message, ics: res.ics }]);
                  if (res.portal_message) {
                    // Send the portal note as a natural follow-up bubble after a short typing beat.
                    setBusy(true);
                    setTimeout(() => {
                      setMsgs((m) => [...m, { from: "penny", text: res.portal_message, portalUrl: res.portal_url }]);
                      setBusy(false);
                    }, 2200);
                  }
                }}
                ensureSession={ensureSession} color={color} />
            )}
            </div>
          </div>

          {/* footer / composer */}
          {screen === "chat" ? (
            <div style={{ borderTop: "1px solid #e2e8f0", background: "#fff" }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "10px 10px 0" }} data-testid="widget-quick-chips">
                {CHIPS.map((c) => (
                  <button key={c.key} onClick={() => chooseAction(c.key)} data-testid={`widget-chip-${c.key}`}
                    style={{ fontSize: 12.5, fontWeight: 600, padding: "6px 12px", borderRadius: 999, cursor: "pointer",
                      border: `1.5px solid ${color}`, color: color, background: "#fff", whiteSpace: "nowrap", transition: "background-color .25s ease, color .25s ease" }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = color; e.currentTarget.style.color = "#fff"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = color; }}>
                    {c.label}
                  </button>
                ))}
              </div>
              <div style={{ padding: 10, display: "flex", gap: 8 }}>
                <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                  data-testid="widget-input" placeholder="Type your message…"
                  style={{ flex: 1, border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px", fontSize: 14, outline: "none" }} />
                <button onClick={send} disabled={busy} data-testid="widget-send"
                  style={{ background: color, color: "#fff", border: "none", borderRadius: 10, padding: "0 16px", fontWeight: 600, cursor: "pointer" }}>Send</button>
              </div>
            </div>
          ) : (
            <div style={{ padding: 10, borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", justifyContent: "space-between" }}>
              <button onClick={resetToMenu} data-testid="widget-menu-back" style={{ background: "transparent", border: "none", color: "#64748b", fontSize: 13, cursor: "pointer" }}>← Menu</button>
              <span style={{ fontSize: 11, color: "#cbd5e1" }}>Powered by {cfg.assistant_name}</span>
            </div>
          )}
          {emailOpen && (
            <div data-testid="widget-email-dialog"
              style={{ position: "absolute", inset: 0, background: "rgba(15,23,42,.45)", display: "flex",
                alignItems: "center", justifyContent: "center", padding: 16, zIndex: 5 }}>
              <div style={{ background: "#fff", borderRadius: 16, padding: 16, width: "100%", maxWidth: 340, boxShadow: "0 20px 60px rgba(0,0,0,.3)" }}>
                {emailSent ? (
                  <div data-testid="widget-email-success">
                    <div style={{ fontSize: 15, fontWeight: 800, color: accent, marginBottom: 8 }}>Message sent</div>
                    <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.5, marginBottom: 14 }}>
                      Thanks{leadName ? `, ${leadName}` : ""}! Our team has your message and will reply by email shortly.
                    </div>
                    <button onClick={() => setEmailOpen(false)} data-testid="widget-email-done"
                      style={{ width: "100%", padding: "10px", background: accent, color: "#fff", border: "none", borderRadius: 10, cursor: "pointer", fontWeight: 700 }}>Done</button>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: accent, marginBottom: 4 }}>Email our team</div>
                    <div style={{ fontSize: 12.5, color: "#64748b", marginBottom: 10, lineHeight: 1.45 }}>
                      Tell us what you'd like to do — reschedule, a question, or any other request — and we'll follow up by email.
                    </div>
                    <textarea value={emailText} onChange={(e) => setEmailText(e.target.value)} data-testid="widget-email-text" rows={4}
                      placeholder="e.g. I'd like to reschedule my consultation to next week…"
                      style={{ width: "100%", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px", fontSize: 14, outline: "none", boxSizing: "border-box", resize: "vertical" }} />
                    <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                      <button onClick={() => setEmailOpen(false)} data-testid="widget-email-cancel"
                        style={{ flex: 1, padding: "10px", background: "#fff", color: "#64748b", border: "1px solid #e2e8f0", borderRadius: 10, cursor: "pointer", fontWeight: 600 }}>Cancel</button>
                      <button onClick={submitEmail} disabled={emailBusy || !emailText.trim()} data-testid="widget-email-send"
                        style={{ flex: 1, padding: "10px", background: accent, color: "#fff", border: "none", borderRadius: 10, cursor: "pointer", fontWeight: 700, opacity: (emailBusy || !emailText.trim()) ? .5 : 1 }}>
                        {emailBusy ? "Sending…" : "Send"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, required }) {
  return (
    <label style={{ display: "block", marginBottom: 10 }}>
      <span style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>{label}{required ? " *" : ""}</span>
      <input value={value} type={type} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        style={{ width: "100%", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px", fontSize: 14, marginTop: 4, outline: "none", boxSizing: "border-box" }} />
    </label>
  );
}

function ContactForm({ cfg, contact, setContact, onSubmit, busy, color }) {
  const req = cfg.required_fields || ["first_name", "last_name", "email"];
  return (
    <div data-testid="widget-contact-form">
      <div style={{ fontSize: 14, color: "#0f172a", marginBottom: 12 }}>
        Before we continue, may I get your contact details? That way, if we get disconnected, our team can follow up and continue assisting you.
      </div>
      <Field label="First Name" value={contact.first_name} onChange={(v) => setContact({ ...contact, first_name: v })} testid="widget-first-name" required={req.includes("first_name")} />
      <Field label="Last Name" value={contact.last_name} onChange={(v) => setContact({ ...contact, last_name: v })} testid="widget-last-name" required={req.includes("last_name")} />
      <Field label="Email Address" type="email" value={contact.email} onChange={(v) => setContact({ ...contact, email: v })} testid="widget-email" required={req.includes("email")} />
      <Field label="Phone Number (optional)" value={contact.phone} onChange={(v) => setContact({ ...contact, phone: v })} testid="widget-phone" />
      <button onClick={onSubmit} disabled={busy} data-testid="widget-contact-submit"
        style={{ width: "100%", background: color, color: "#fff", border: "none", borderRadius: 10, padding: "12px", fontWeight: 700, cursor: "pointer", marginTop: 4 }}>
        {busy ? "Saving…" : "Continue"}
      </button>
    </div>
  );
}

function QuoteForm({ onDone, busy, color }) {
  const [ans, setAns] = useState({});
  return (
    <div data-testid="widget-quote-form">
      <div style={{ fontSize: 14, color: "#0f172a", marginBottom: 12 }}>A few quick questions so I can narrow your range and route you to the right team.</div>
      {QUOTE_QS.map(([k, q]) => (
        <Field key={k} label={q} value={ans[k] || ""} onChange={(v) => setAns({ ...ans, [k]: v })} testid={`widget-quote-${k}`} />
      ))}
      <button onClick={() => onDone(ans)} disabled={busy} data-testid="widget-quote-submit"
        style={{ width: "100%", background: color, color: "#fff", border: "none", borderRadius: 10, padding: "12px", fontWeight: 700, cursor: "pointer", marginTop: 4 }}>
        {busy ? "Working…" : "Get my estimate"}
      </button>
    </div>
  );
}

function ConsultForm({ cfg, firmId, contact, prequal, onBooked, ensureSession, color }) {
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState(null);
  const [sel, setSel] = useState(null);
  const [tz, setTz] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York");
  const [form, setForm] = useState({ ...contact,
    assist_with: prequal?.assist_with || "", tax_years: prequal?.tax_years || "",
    irs_notices: prequal?.irs_notices || "", citizenship: prequal?.citizenship || "",
    tax_residence: prequal?.tax_residence || prequal?.residence || "", first_time: true });
  const [busy, setBusy] = useState(false);
  // Which answers Penny already captured in chat (snapshot at open) — so she can acknowledge them.
  const [prefilledCount] = useState(() =>
    ["assist_with", "tax_years", "irs_notices", "citizenship", "tax_residence"]
      .filter((k) => (prequal?.[k] || (k === "tax_residence" && prequal?.residence) || "").toString().trim()).length);

  useEffect(() => { setForm((f) => ({ ...f, ...contact })); }, [contact]);
  useEffect(() => {
    if (!date) return; setSlots(null); setSel(null);
    get(`/consult/slots?firm_id=${firmId}&date=${date}`).then((d) => setSlots(d.slots || [])).catch(() => setSlots([]));
  }, [date, firmId]);

  const prequalDone = ["assist_with", "tax_years", "irs_notices", "citizenship", "tax_residence"].every((k) => (form[k] || "").trim());
  const canBook = prequalDone && !!sel && !busy;

  const book = async () => {
    if (!canBook) return;
    setBusy(true);
    try {
      const cid = await ensureSession("consult");
      const res = await post("/consult/book", {
        conversation_id: cid, firm_id: firmId, first_name: form.first_name, last_name: form.last_name,
        email: form.email, phone: form.phone, timezone: tz,
        date, start_time: sel.time, staff_id: sel.staff_id, first_time: form.first_time,
        assist_with: form.assist_with, tax_years: form.tax_years, irs_notices: form.irs_notices,
        citizenship: form.citizenship, tax_residence: form.tax_residence,
      });
      onBooked(res);
    } catch (e) { onBooked({ message: "Sorry, I couldn't complete the booking. Our team will reach out to schedule." }); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="widget-consult-form">
      <div style={{ fontSize: 14, color: "#0f172a", marginBottom: 6, fontWeight: 600 }}>Book a Free Consult</div>
      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>
        Your first {cfg.consult?.free_minutes || 30}-minute consultation is free.
      </div>
      {prefilledCount > 0 ? (
        <div data-testid="widget-consult-prefill-note"
          style={{ fontSize: 12.5, color: "#0f172a", background: "#eef6ff", border: "1px solid #dbeafe", borderRadius: 10, padding: "10px 12px", marginBottom: 12, lineHeight: 1.45 }}>
          {(contact?.first_name || "").trim() ? `Thanks, ${contact.first_name.trim()}! ` : ""}I've gone ahead and filled in what you already shared with me — just pick a date and time that works for you and you're all set! Feel free to tweak anything below if needed.
        </div>
      ) : (
        <div style={{ fontSize: 12, color: "#475569", marginBottom: 10 }}>A few quick questions so our team is fully prepared for your call.</div>
      )}
      <Field label="What would you like our team to assist with?" value={form.assist_with} onChange={(v) => setForm({ ...form, assist_with: v })} testid="widget-consult-assist" required />
      <Field label="Which Tax Years do you need help with?" value={form.tax_years} onChange={(v) => setForm({ ...form, tax_years: v })} testid="widget-consult-tax-years" required />
      <Field label="Have you received any notices from the IRS or other tax authority?" value={form.irs_notices} onChange={(v) => setForm({ ...form, irs_notices: v })} testid="widget-consult-irs-notices" required />
      <Field label="What is your citizenship?" value={form.citizenship} onChange={(v) => setForm({ ...form, citizenship: v })} testid="widget-consult-citizenship" required />
      <Field label="What is your Tax Residence?" value={form.tax_residence} onChange={(v) => setForm({ ...form, tax_residence: v })} testid="widget-consult-tax-residence" required />
      <Field label="Preferred date" type="date" value={date} onChange={setDate} testid="widget-consult-date" required />
      {date && (
        <div style={{ marginBottom: 10 }}>
          <span style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>Available times ({tz})</span>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }} data-testid="widget-consult-slots">
            {slots === null ? <span style={{ fontSize: 13, color: "#94a3b8" }}>Loading…</span> :
             slots.length === 0 ? <span style={{ fontSize: 13, color: "#94a3b8" }}>No open times — try another day.</span> :
             slots.map((s) => (
               <button key={`${s.time}-${s.staff_id}`} onClick={() => setSel(s)} data-testid={`widget-slot-${s.time}`}
                 style={{ fontSize: 13, padding: "6px 10px", borderRadius: 8, cursor: "pointer",
                   border: sel?.time === s.time ? `2px solid ${color}` : "1px solid #e2e8f0",
                   background: sel?.time === s.time ? color : "#fff", color: sel?.time === s.time ? "#fff" : "#0f172a" }}>{s.time}</button>
             ))}
          </div>
        </div>
      )}
      {!prequalDone && (
        <div style={{ fontSize: 11.5, color: "#94a3b8", marginBottom: 8 }} data-testid="widget-consult-gate-hint">
          Please answer all questions above to confirm your consult.
        </div>
      )}
      <button onClick={book} disabled={!canBook} data-testid="widget-consult-book"
        style={{ width: "100%", background: color, color: "#fff", border: "none", borderRadius: 10, padding: "12px", fontWeight: 700, cursor: !canBook ? "default" : "pointer", opacity: !canBook ? .5 : 1, marginTop: 4 }}>
        {busy ? "Booking…" : "Confirm free consult"}
      </button>
    </div>
  );
}
